import { useCallback, useMemo, useState } from "react";
import Spinner from "../../../common/components/Spinner/Spinner";
import { NOTIFICATIONS_SETTINGS_PAGE } from "../../../utils/url/staticUrls";
import { NotificationsFiltersModal } from "./components/NotificationsFiltersModal";
import { IMG_EMPTY_NOTIFICATIONS } from "../../../utils/url/staticResources";
import Pagination from "~/features/search/components/common/Pagination";
import useMediaQuery from "../../../common/hooks/useMediaQuery";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { useOnMount } from "../../../common/hooks/useOnMount";
import {
  deleteNotificationsThunk,
  initialiseNotificationsPageThunk,
  markNotificationsAsReadThunk,
} from "../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsNotificationsError,
  selectIsNotificationsLoaded,
  selectIsNotificationsLoading,
  selectNotifications,
} from "../redux/NotificationsSelectors";
import { MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import {
  fetchNavigationFilters,
  type NotificationFilterEntry,
  NotificationsFilters,
  useNotificationFiltering,
  useNotificationFilters,
} from "~/features/notifications/NotificationsPage/components/NotificationsFilters";
import { Box, Divider, Stack, Typography, styled } from "~/common/mui";
import { Link, LinkButton } from "~/common/components/Link";
import { NotificationsList } from "~/features/notifications/NotificationsPage/components/NotificationsList";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import type { NotificationData } from "~/features/notifications/Messages";

const PageGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: "var(--gutter)",
  [theme.breakpoints.down("tablet")]: {
    "& > *": {
      gridColumn: "1 / -1",
    },
  },
}));

const FiltersSlot = styled(Box)(({ theme }) => ({
  paddingRight: theme.spacing(5),
  gridColumn: "1 / span 4",
  [theme.breakpoints.down("tablet")]: {
    display: "none",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "1 / span 3",
  },
}));

const NotificationsSlot = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("tablet")]: {
    gridColumn: "5 / -1",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "4 / -1",
  },
}));

const PAGE_SIZE = 10;

const getPageAmount = (elementCount: number) => Math.ceil(elementCount / PAGE_SIZE);

export interface NotificationsPageViewProps {
  isMinTablet: boolean;
  notifications: NotificationData[];
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
  filters: NotificationFilterEntry[];
  onFilterChange: (filter: NotificationFilterEntry) => void;
  onReplaceFilters: (filters: NotificationFilterEntry[]) => void;
  onDeleteNotifications: (uids: string | string[]) => void;
  onMarkNotificationsAsRead: (uids: string | string[], isRead: boolean) => void;
}

export function NotificationsPageView({
  isMinTablet,
  notifications,
  isLoading,
  isLoaded,
  isError,
  filters,
  onFilterChange,
  onReplaceFilters,
  onDeleteNotifications,
  onMarkNotificationsAsRead,
}: NotificationsPageViewProps) {
  const { t } = useTranslation();
  const { val: isModalOpened, setFalse: closeModal, setTrue: openModal } = useBooleanState(false);
  const [page, setPage] = useState(0);

  const disableInteractions = !isLoaded;

  const filteredNotifications = useNotificationFiltering(filters, notifications);

  const numberOfPages = getPageAmount(filteredNotifications.length);
  const visiblePage = useMemo(
    () => filteredNotifications.slice(PAGE_SIZE * page, PAGE_SIZE * (page + 1)),
    [filteredNotifications, page],
  );

  const deleteNotifications = useCallback(
    (uids: string | string[]) => {
      const uidList = Array.isArray(uids) ? uids : [uids];
      onDeleteNotifications(uidList);
      const remainingAmount = filteredNotifications.length - uidList.length;
      const remainingPages = getPageAmount(remainingAmount);
      if (remainingPages < numberOfPages && page === numberOfPages - 1) {
        setPage((curPage) => curPage - 1);
      }
    },
    [filteredNotifications.length, numberOfPages, onDeleteNotifications, page],
  );

  const onApplyNewFilters = (newFilters: NotificationFilterEntry[]) => {
    closeModal();
    onReplaceFilters(newFilters);
  };

  const changeFilter = (filter: NotificationFilterEntry) => {
    onFilterChange(filter);
    setPage(0);
  };

  return (
    <>
      <PageGrid>
        {isMinTablet && (
          <FiltersSlot>
            <NotificationsFilters disabled={disableInteractions} filters={filters} onFilterChange={changeFilter} />
          </FiltersSlot>
        )}

        <NotificationsSlot>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="headlineSmall" sx={{ margin: 0 }}>
              {t("notificationsPage.title", { count: filteredNotifications.length })}
            </Typography>
            <Stack gap={2} direction={"row"}>
              <Link
                iconBefore={<SettingsIcon  />}
                href={NOTIFICATIONS_SETTINGS_PAGE}
                data-ta-spec={"notification-settings"}
              >
                {t("notificationsPage.settings.title")}
              </Link>
              {!isMinTablet && (
                <LinkButton
                  onClick={openModal}
                  disabled={disableInteractions}
                  iconBefore={<TuneIcon  />}
                >
                  {t("notificationsPage.settings.filter")}
                </LinkButton>
              )}
            </Stack>
          </Box>
          <Divider sx={{ mt: 1, mb: 3 }} />

          {isLoading && <Spinner className="mb-3" />}

          {isLoaded && visiblePage.length > 0 && (
            <NotificationsList
              data={visiblePage}
              onDeleteItems={deleteNotifications}
              onMarkAsRead={onMarkNotificationsAsRead}
            />
          )}

          {numberOfPages > 1 && (
            <PaginationContainer>
              <Pagination
                classes={{ root: "scha__srp__pagination" }}
                pageAmount={numberOfPages}
                currentPage={page}
                onPageChange={setPage}
                goToIdAfterChange={"#resultsHeader"}
              />
            </PaginationContainer>
          )}

          {isError && <Alert severity="error">{t("notificationsPage.error")}</Alert>}

          {isLoaded && !visiblePage.length && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 2 }}>
              <Box component="img" src={IMG_EMPTY_NOTIFICATIONS} alt="no notifications" sx={{ mb: 2 }} />
              <Typography variant="headlineSmall" sx={{ margin: 0 }}>
                {t("notification.noResults.title")}
              </Typography>
              <Typography variant="bodyMedium" sx={{ margin: 0 }}>
                {t("notification.noResults.description")}
              </Typography>
            </Box>
          )}
        </NotificationsSlot>
      </PageGrid>
      {isModalOpened && (
        <NotificationsFiltersModal currentFilters={filters} onClose={closeModal} onApply={onApplyNewFilters} />
      )}
    </>
  );
}

export function NotificationsPage() {
  const dispatch = useDispatch();

  const isMinTable = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  const notifications = useSelector(selectNotifications);
  const isLoading = useSelector(selectIsNotificationsLoading);
  const isLoaded = useSelector(selectIsNotificationsLoaded);
  const isError = useSelector(selectIsNotificationsError);

  const markNotificationsAsRead = useCallback(
    (uids: string | string[], isRead: boolean) => dispatch(markNotificationsAsReadThunk(uids, isRead)),
    [dispatch],
  );

  useOnMount(() => dispatch(initialiseNotificationsPageThunk()));

  const { filters, changeFilter, replaceFilters } = useNotificationFilters(fetchNavigationFilters);

  const deleteNotifications = useCallback(
    (uids: string | string[]) => {
      dispatch(deleteNotificationsThunk(uids));
    },
    [dispatch],
  );

  return (
    <NotificationsPageView
      isMinTablet={isMinTable}
      notifications={notifications}
      isLoading={isLoading}
      isLoaded={isLoaded}
      isError={isError}
      filters={filters}
      onFilterChange={changeFilter}
      onReplaceFilters={replaceFilters}
      onDeleteNotifications={deleteNotifications}
      onMarkNotificationsAsRead={markNotificationsAsRead}
    />
  );
}

const PaginationContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
}));
